import { ReactNode } from "react";

export type StatGradientTone =
  | "orange"
  | "pink"
  | "blue"
  | "purple"
  | "teal"
  | "cyan"
  | "yellow";

interface StatGradientCardProps {
  title: string;
  subtitle: string;
  value: number | string;
  icon: ReactNode;
  tone?: StatGradientTone;
  active?: boolean;
  onClick?: () => void;
}

const iconToneClasses: Record<StatGradientTone, string> = {
  orange: "bg-[#FFF0DE] text-[#F08A3A]",
  pink: "bg-[#FFE8EC] text-[#E45A6A]",
  blue: "bg-[#E8F1FB] text-[#3A7FC0]",
  purple: "bg-[#F0EAFB] text-[#7A5AC0]",
  teal: "bg-[#E8F9F5] text-[#1dc399]",
  cyan: "bg-[#E6F7F8] text-[#2AABB8]",
  yellow: "bg-[#FFF8E6] text-[#E6A817]",
};

export default function StatGradientCard({
  title,
  subtitle,
  value,
  icon,
  tone = "teal",
  active = false,
  onClick,
}: StatGradientCardProps) {
  const className = [
    "relative flex h-[130px] w-full flex-col justify-between rounded-2xl border bg-white p-4 text-right shadow-sm transition-all",
    onClick ? "cursor-pointer hover:border-primaryVar-300 hover:shadow-md" : "border-gray-100",
    active ? "border-primaryVar-500 ring-1 ring-primaryVar-500" : "border-gray-100",
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <>
      <div className="flex items-start justify-between gap-3">
        <span className="text-sm font-medium text-gray-800">{title}</span>
        <div
          className={[
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
            iconToneClasses[tone],
          ].join(" ")}
        >
          {icon}
        </div>
      </div>

      <div>
        <div className="text-3xl font-bold leading-none tracking-tight text-gray-900">
          {typeof value === "number" ? value.toLocaleString("fa-IR") : value}
        </div>
        <div className="mt-2 text-xs text-gray-500">{subtitle}</div>
      </div>
    </>
  );

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={className}>
        {content}
      </button>
    );
  }

  return <div className={className}>{content}</div>;
}
